import React, { useState, useEffect, useMemo } from "react";
import { TableRowData } from "./data-table";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { X, Download } from "lucide-react";
import { Badge } from "./ui/badge";
import {
  MovementActivity,
  ProjectType,
  updateMovementStatus,
  updateMovement,
  getMovementActivity,
  extendMovementDaysHired,
  cancelMovementActivity,
  getInvoices,
  Invoice,
  setMovementInvoices,
} from "../lib/api";
import { format } from "date-fns";
import { cn, getBadgeVariant, getBackendErrorMessage } from "../lib/utils";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "./ui/table";
import ActivityStages, { type Stage } from "./activity-stages";
import ReturningNoteDrawer from "./returning-note-drawer";
import DeliveryNoteDrawer from "./delivery-note-drawer";
import SellNoteDrawer from "./sell-note-drawer";
import TransferDetailsDrawer from "./transfer-details-drawer";
import DetailsOfReceivedDrawer from "./details-of-received-drawer";
import WarehouseTransferDetailsDrawer from "./warehouse-transfer-details-drawer";
import LostItemsDetailsDrawer from "./lost-items-details-drawer";
import { Icons } from "./ui/icons";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import SelectInput from "./select-input";
import SearchableSelectInput from "./searchable-select-input";
import UploadDocumentModal from "./upload-document-modal";
import InvoiceDrawer from "./invoice-drawer";
import { Modal } from "./ui/modal";
import { DELIVER, RETURN, SELL } from "../pages/AddActivity";
import { toast } from "sonner";

interface ActivityDetailsDrawerProps {
  selectedActivityDetails: TableRowData | null;
  onClose: () => void;
  onRefresh?: () => void;
}

export interface MovementHistory {
  initiator: string;
  send_to: string;
  stage: string;
  priority: string;
  notes: string;
  status: string;
}

export const getBadgeColor = (activity: string) => {
  switch (activity) {
    case "Return":
      return "secondary";
    case "Transfer":
      return "outline";
    case "Purchase":
      return "open";
    case "Delivery":
      return "onhold";
    case "Dispose":
      return "destructive";
    case "Sell":
      return "closed";
    default:
      return "default";
  }
};

const getMovementInvoices = (movement?: MovementActivity): Invoice[] => {
  if (!movement) return [];
  if (movement.invoices?.length) return movement.invoices;
  const legacy = (movement as { invoice?: Invoice | null }).invoice;
  return legacy ? [legacy] : [];
};

const formatInvoiceTotal = (invoice: Invoice): string => {
  const total = invoice.totalAmount ?? invoice.amount ?? "0";
  return `${invoice.currencyType} ${parseFloat(String(total)).toLocaleString()}`;
};

const toDateInputValue = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  return new Date(trimmed).toISOString().slice(0, 10);
};

const addDaysToDateInput = (dateStr: string, days: number): string => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** Inclusive day count for YYYY-MM-DD range (Jul 1–Jul 10 = 10). */
const inclusiveDaysBetween = (
  dateFrom: string,
  dateTo: string,
): number | null => {
  if (!dateFrom || !dateTo) return null;
  const fromMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateFrom);
  const toMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateTo);
  if (!fromMatch || !toMatch) return null;
  const from = Date.UTC(
    Number(fromMatch[1]),
    Number(fromMatch[2]) - 1,
    Number(fromMatch[3]),
  );
  const to = Date.UTC(
    Number(toMatch[1]),
    Number(toMatch[2]) - 1,
    Number(toMatch[3]),
  );
  if (to < from) return null;
  return Math.floor((to - from) / (24 * 60 * 60 * 1000)) + 1;
};

/** Autofill extension start: hire start if no days hired yet, else day after current end. */
const getDefaultExtensionDateFrom = (
  movement?: MovementActivity | null,
): string => {
  if (!movement) return "";
  const hireStart =
    toDateInputValue(movement.plannedDate) ||
    toDateInputValue(movement.createdAt);
  if (!hireStart) return "";
  const daysHired = Number(movement.daysHired ?? movement.days_hired ?? 0);
  if (!Number.isFinite(daysHired) || daysHired <= 0) return hireStart;
  return addDaysToDateInput(hireStart, daysHired);
};

const invoiceToTableRow = (invoice: Invoice): TableRowData => ({
  id: invoice.id,
  reference: invoice.reference,
  customer: invoice.customer?.fullName ?? "N/A",
  site: invoice.site?.name ?? "N/A",
  amount: `${invoice.currencyType} ${parseFloat(invoice.amount).toLocaleString()}`,
  totalAmount: `${invoice.currencyType} ${parseFloat(invoice.totalAmount).toLocaleString()}`,
  status: invoice.status,
  dueDate: format(new Date(invoice.dueDate), "MMM dd, yyyy"),
  issueDate: format(new Date(invoice.issueDate), "MMM dd, yyyy"),
  raw_data: invoice,
});

const MobileDetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 px-4 py-3">
    <span className="shrink-0 text-sm text-gray-500">{label}</span>
    <span className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
      {value || "—"}
    </span>
  </div>
);

type MovementItem = NonNullable<MovementActivity["items"]>[number];

const getItemParticulars = (item: MovementItem) =>
  [
    item.inventory?.description,
    item.inventory?.length != null &&
    item.inventory.length !== "" &&
    String(item.inventory.length) !== "undefined"
      ? String(item.inventory.length)
      : null,
  ]
    .filter(Boolean)
    .join(" ") || "—";

const getItemTotalWeight = (item: MovementItem): number | null => {
  const uw = item.inventory?.unitWeight;
  const qty = item.totalQuantity ?? 0;
  if (uw == null || uw === "" || !qty) return null;
  const n = Number(uw);
  return Number.isFinite(n) ? n * qty : null;
};

const formatWeight = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const MobileItemStat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <p className="text-gray-500">{label}</p>
    <p className="truncate font-medium text-gray-900">{value ?? "—"}</p>
  </div>
);

const MobileItemsList: React.FC<{ items: MovementItem[] }> = ({ items }) => {
  if (items.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        No items found
      </div>
    );
  }

  const grandTotal = items.reduce(
    (sum, item) => sum + (getItemTotalWeight(item) ?? 0),
    0,
  );

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const totalWeight = getItemTotalWeight(item);
        const unitWeight = item.inventory?.unitWeight;
        return (
          <div
            key={item.id}
            className="rounded-xl border border-gray-200 bg-white p-3"
          >
            <p className="break-words text-sm font-semibold text-gray-900">
              {getItemParticulars(item)}
            </p>
            <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2 text-xs">
              <MobileItemStat label="Total Qty" value={item.totalQuantity} />
              <MobileItemStat label="Good" value={item.goodQuantity} />
              <MobileItemStat label="Damaged" value={item.damagedQuantity} />
              <MobileItemStat
                label="Unit Weight"
                value={
                  unitWeight != null && unitWeight !== ""
                    ? String(unitWeight)
                    : "—"
                }
              />
              <MobileItemStat
                label="Total Weight"
                value={totalWeight != null ? formatWeight(totalWeight) : "—"}
              />
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between rounded-xl bg-gray-100 px-3 py-2.5 text-sm font-semibold">
        <span>Grand Total Weight</span>
        <span>{grandTotal > 0 ? formatWeight(grandTotal) : "—"}</span>
      </div>
    </div>
  );
};

const BodyScroll: React.FC<{
  isMobile: boolean;
  children: React.ReactNode;
}> = ({ isMobile, children }) =>
  isMobile ? (
    <>{children}</>
  ) : (
    <ScrollArea className="h-full">{children}</ScrollArea>
  );

const ActivityDetailsDrawer: React.FC<ActivityDetailsDrawerProps> = ({
  selectedActivityDetails,
  onClose,
  onRefresh,
}) => {
  const isMobile = useIsMobile();
  const [returningNoteDetails, setReturningNoteDetails] =
    useState<TableRowData | null>(null);
  const [deliveryNoteDetails, setDeliveryNoteDetails] =
    useState<TableRowData | null>(null);
  const [sellNoteDetails, setSellNoteDetails] = useState<TableRowData | null>(
    null,
  );
  const [transferDetails, setTransferDetails] = useState<TableRowData | null>(
    null,
  );
  const [warehouseTransferDetails, setWarehouseTransferDetails] =
    useState<TableRowData | null>(null);
  const [detailsOfReceived, setDetailsOfReceived] =
    useState<TableRowData | null>(null);
  const [lostItemsDetails, setLostItemsDetails] = useState<TableRowData | null>(
    null,
  );
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [selectedOneOffInvoice, setSelectedOneOffInvoice] =
    useState<TableRowData | null>(null);
  const [linkInvoiceId, setLinkInvoiceId] = useState("");
  const [invoiceSelectOptions, setInvoiceSelectOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [isLinkingInvoice, setIsLinkingInvoice] = useState(false);
  const [isSavingInvoices, setIsSavingInvoices] = useState(false);
  const [unlinkInvoiceTarget, setUnlinkInvoiceTarget] = useState<
    { type: "one"; id: string; reference: string } | { type: "all" } | null
  >(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isCancellingMovement, setIsCancellingMovement] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [isUpdatingDriverVehicle, setIsUpdatingDriverVehicle] = useState(false);
  const [isUpdatingProjectType, setIsUpdatingProjectType] = useState(false);
  const [isExtendingDaysHired, setIsExtendingDaysHired] = useState(false);
  const [projectType, setProjectType] = useState("");
  const [extendDateFrom, setExtendDateFrom] = useState("");
  const [extendDateTo, setExtendDateTo] = useState("");
  const [extendDaysNotes, setExtendDaysNotes] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverPhoneNumber, setDriverPhoneNumber] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [returnedBy, setReturnedBy] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [returnedDate, setReturnedDate] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [isLoadingMovementDetails, setIsLoadingMovementDetails] =
    useState(false);
  const [currentMovementData, setCurrentMovementData] = useState<
    MovementActivity | undefined
  >(undefined);

  // Get the raw movement activity data
  const movementData = currentMovementData;
  const linkedInvoices = getMovementInvoices(movementData);
  const linkedInvoiceIds = useMemo(
    () => new Set(linkedInvoices.map((inv) => inv.id)),
    [linkedInvoices],
  );
  const availableInvoiceOptions = useMemo(
    () =>
      invoiceSelectOptions.filter((opt) => !linkedInvoiceIds.has(opt.value)),
    [invoiceSelectOptions, linkedInvoiceIds],
  );
  const movementStatusUpper = (movementData?.status ?? "").toUpperCase();
  const isMovementTerminal =
    movementStatusUpper === "CLOSED" || movementStatusUpper === "CANCELLED";
  const isCancelledMovement = movementStatusUpper === "CANCELLED";
  const canCancelMovement = Boolean(movementData?.id) && !isMovementTerminal;
  const isReturnSubType =
    movementData?.activitySubType === "RETURN" ||
    selectedActivityDetails?.activity_sub_type === RETURN.toUpperCase();
  const isPurchaseSubType =
    movementData?.activitySubType === "PURCHASE" ||
    selectedActivityDetails?.activity_sub_type === "PURCHASE";
  const isTransferOutflowSubType =
    (movementData?.activitySubType === "TRANSFER" ||
      selectedActivityDetails?.activity_sub_type === "TRANSFER") &&
    ((movementData as any)?.activityType === "OUTFLOW" ||
      selectedActivityDetails?.activity_type === "OUTFLOW");
  const isTransferInflowSubType =
    (movementData?.activitySubType === "TRANSFER" ||
      selectedActivityDetails?.activity_sub_type === "TRANSFER") &&
    ((movementData as any)?.activityType === "INFLOW" ||
      selectedActivityDetails?.activity_type === "INFLOW");
  const isDeliverSubType =
    movementData?.activitySubType === "DELIVER" ||
    selectedActivityDetails?.activity_sub_type === DELIVER.toUpperCase();
  const canExtendDaysHired = isDeliverSubType || isTransferOutflowSubType;
  const isReturnOrTransferOutflow = isReturnSubType || isTransferOutflowSubType;
  const isApprovedBySubtype =
    isPurchaseSubType || isTransferOutflowSubType || isTransferInflowSubType;

  const applyFormFieldsFromMovement = (m?: MovementActivity) => {
    setDriverName(m?.driverName ?? m?.driver_name ?? "");
    setDriverPhoneNumber(m?.driverPhoneNumber ?? m?.driver_phone_number ?? "");
    setVehicleNumber(m?.vehicleNumber ?? m?.vehicle_number ?? "");
    setReturnedBy(m?.returnedBy ?? m?.returned_by ?? "");
    setReceivedBy(m?.receivedBy ?? m?.received_by ?? "");
    setApprovedBy(m?.approvedBy ?? m?.approved_by ?? "");
    const rd = m?.returnedDate ?? m?.returned_date;
    setReturnedDate(rd ? new Date(rd).toISOString().slice(0, 10) : "");
    setPlannedDate(toDateInputValue(m?.plannedDate));
    setProjectType(m?.projectType?.trim() ? m.projectType : "__none__");
    setExtendDateFrom(getDefaultExtensionDateFrom(m));
    setExtendDateTo("");
    setExtendDaysNotes("");
  };

  // Load full movement details on open (list API returns a light payload).
  useEffect(() => {
    const movementId = selectedActivityDetails?.id
      ? String(selectedActivityDetails.id)
      : "";
    if (!movementId) {
      setCurrentMovementData(undefined);
      setIsLoadingMovementDetails(false);
      return;
    }

    let cancelled = false;
    const lightRaw = (selectedActivityDetails as any)?.raw_data as
      | MovementActivity
      | undefined;
    setCurrentMovementData(lightRaw);
    applyFormFieldsFromMovement(lightRaw);
    setIsLoadingMovementDetails(true);

    (async () => {
      try {
        const full = await getMovementActivity(movementId);
        if (cancelled) return;
        setCurrentMovementData(full);
        applyFormFieldsFromMovement(full);
        if (selectedActivityDetails) {
          (selectedActivityDetails as any).raw_data = full;
          (selectedActivityDetails as any).status = full.status;
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            getBackendErrorMessage(error, "Failed to load movement details"),
          );
        }
      } finally {
        if (!cancelled) setIsLoadingMovementDetails(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedActivityDetails?.id]);

  useEffect(() => {
    if (!selectedActivityDetails) setCancelConfirmOpen(false);
  }, [selectedActivityDetails]);

  useEffect(() => {
    if (isCancelledMovement) setShowUploadDocument(false);
  }, [isCancelledMovement]);

  useEffect(() => {
    if (!selectedActivityDetails) {
      setInvoiceSelectOptions([]);
      setLinkInvoiceId("");
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoadingInvoices(true);
      try {
        const res = await getInvoices(1, 500, "");
        if (cancelled) return;
        setInvoiceSelectOptions(
          res.data.map((inv) => ({
            value: inv.id,
            label: `${inv.reference} — ${inv.customer?.fullName ?? "N/A"} (${inv.currencyType} ${parseFloat(inv.totalAmount || "0").toLocaleString()})`,
          })),
        );
      } catch (error) {
        if (!cancelled) {
          toast.error(getBackendErrorMessage(error, "Failed to load invoices"));
        }
      } finally {
        if (!cancelled) setIsLoadingInvoices(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedActivityDetails]);

  useEffect(() => {
    setLinkInvoiceId("");
    setUnlinkInvoiceTarget(null);
  }, [movementData?.id, linkedInvoices.length]);

  const projectTypeOptions = [
    { value: "__none__", label: "—" },
    ...Object.values(ProjectType).map((v) => ({ value: v, label: v })),
  ];

  // Map status history to stages (normalized uppercase). Cancelled movements use a 4th "CANCELLED" step instead of CLOSED.
  const getStagesFromStatusHistory = (): Stage[] => {
    const norm = (s: string) => (s || "").toUpperCase();
    const pipeline = ["DRAFT", "INITIATED", "APPROVED"] as const;
    const allStages = ["DRAFT", "INITIATED", "APPROVED", "CLOSED"] as const;
    const currentStatus = norm(movementData?.status ?? "");

    const reachedStatuses = new Set<string>();
    if (movementData?.statusHistory) {
      movementData.statusHistory.forEach((history) => {
        reachedStatuses.add(norm(history.newStatus));
        reachedStatuses.add(norm(history.previousStatus));
      });
    }
    if (currentStatus) reachedStatuses.add(currentStatus);

    if (currentStatus === "CANCELLED") {
      const hist = [...(movementData?.statusHistory ?? [])].sort(
        (a, b) =>
          new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
      );
      const cancelEntry = hist.find((h) => norm(h.newStatus) === "CANCELLED");
      let furthest = -1;
      if (cancelEntry) {
        const before = norm(cancelEntry.previousStatus);
        const idx = (pipeline as readonly string[]).indexOf(before);
        if (idx >= 0) furthest = idx;
      }
      if (furthest < 0) {
        pipeline.forEach((st, i) => {
          if (reachedStatuses.has(st)) furthest = Math.max(furthest, i);
        });
      }

      return [
        ...pipeline.map((stage, i) => {
          let status: Stage["status"];
          let statusText: string;
          if (furthest < 0) {
            status = "not-started";
            statusText = "Not started";
          } else if (i < furthest) {
            status = "complete";
            statusText = "Complete";
          } else if (i === furthest) {
            status = "complete";
            statusText = "Complete";
          } else {
            status = "not-started";
            statusText = "Not reached";
          }
          return { name: stage, status, statusText };
        }),
        { name: "CANCELLED", status: "cancelled", statusText: "Cancelled" },
      ];
    }

    return allStages.map((stage) => {
      const stagesList = allStages as readonly string[];
      const stageIndex = stagesList.indexOf(stage);
      const currentIndex = stagesList.indexOf(currentStatus);

      let status: Stage["status"] = "not-started";
      let statusText = "Not Started";

      if (reachedStatuses.has(stage)) {
        if (currentStatus === "CLOSED" && stage === "CLOSED") {
          status = "complete";
          statusText = "Complete";
        } else if (stage === currentStatus && currentStatus !== "CLOSED") {
          status = "in-progress";
          statusText = "In Progress";
        } else if (
          stageIndex < currentIndex ||
          (currentIndex === -1 && reachedStatuses.has(stage))
        ) {
          status = "complete";
          statusText = "Complete";
        }
      }

      return {
        name: stage,
        status,
        statusText,
      };
    });
  };

  const stages = getStagesFromStatusHistory();
  const currentStage = movementStatusUpper || "DRAFT";

  // Get next status in the sequence
  const getNextStatus = (currentStatus: string): string | null => {
    const statusSequence = ["DRAFT", "INITIATED", "APPROVED", "CLOSED"];
    const normalized = (currentStatus || "").toUpperCase();
    const currentIndex = statusSequence.indexOf(normalized);
    if (currentIndex >= 0 && currentIndex < statusSequence.length - 1) {
      return statusSequence[currentIndex + 1];
    }
    return null; // Already at final status
  };

  const getNextStageButtonLabel = (nextStatus: string | null): string => {
    if (!nextStatus) return "Move to next stage";
    const labels: Record<string, string> = {
      INITIATED: "Mark as Initiated",
      APPROVED: "Approve",
      CLOSED: "Close movement",
    };
    return labels[nextStatus] ?? `Move to ${nextStatus}`;
  };

  const confirmCancelMovement = async () => {
    if (!movementData?.id) return;
    try {
      setIsCancellingMovement(true);
      const updatedMovement = await cancelMovementActivity(movementData.id);
      setCurrentMovementData(updatedMovement);
      if (selectedActivityDetails) {
        (selectedActivityDetails as any).raw_data = updatedMovement;
        (selectedActivityDetails as any).status = updatedMovement.status;
      }
      if (onRefresh) await onRefresh();
      toast.success("Movement cancelled");
      setCancelConfirmOpen(false);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to cancel movement"));
    } finally {
      setIsCancellingMovement(false);
    }
  };

  const applyMovementUpdate = async (updated: MovementActivity) => {
    setCurrentMovementData(updated);
    if (selectedActivityDetails) {
      (selectedActivityDetails as any).raw_data = updated;
    }
    if (onRefresh) await onRefresh();
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedOneOffInvoice(invoiceToTableRow(invoice));
  };

  const persistMovementInvoices = async (
    invoiceIds: string[],
    successMessage: string,
  ) => {
    if (!movementData?.id) return;
    try {
      setIsSavingInvoices(true);
      const updated = await setMovementInvoices(movementData.id, invoiceIds);
      await applyMovementUpdate(updated);
      toast.success(successMessage);
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to update linked invoices"),
      );
      throw error;
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleLinkInvoice = async () => {
    if (!movementData?.id || !linkInvoiceId) return;
    if (linkedInvoiceIds.has(linkInvoiceId)) {
      toast.error("This invoice is already linked");
      return;
    }
    try {
      const merged = [...linkedInvoices.map((inv) => inv.id), linkInvoiceId];
      await persistMovementInvoices(merged, "Invoice linked");
      setLinkInvoiceId("");
    } catch {
      // toast shown in persistMovementInvoices
    }
  };

  const confirmUnlinkInvoices = async () => {
    if (!movementData?.id || !unlinkInvoiceTarget) return;
    try {
      if (unlinkInvoiceTarget.type === "all") {
        await persistMovementInvoices([], "All invoices unlinked");
      } else {
        const remaining = linkedInvoices
          .map((inv) => inv.id)
          .filter((id) => id !== unlinkInvoiceTarget.id);
        await persistMovementInvoices(remaining, "Invoice unlinked");
      }
      setUnlinkInvoiceTarget(null);
    } catch {
      // toast shown in persistMovementInvoices
    }
  };

  const handleMoveToNextStage = async () => {
    if (!movementData?.id) return;

    const nextStatus = getNextStatus(movementData.status);
    if (!nextStatus) {
      toast.error("Already at final status");
      return;
    }

    try {
      setIsUpdatingStatus(true);
      const updatedMovement = await updateMovementStatus(movementData.id, {
        status: nextStatus,
      });

      // Update local state with new data
      setCurrentMovementData(updatedMovement);

      // Refresh parent component data if callback provided (this will update the table)
      if (onRefresh) {
        await onRefresh();
        // After parent refresh, update selectedActivityDetails if it still exists
        // The parent component will handle updating the selectedActivityDetails
      } else {
        // If no refresh callback, just update local state
        if (selectedActivityDetails) {
          (selectedActivityDetails as any).raw_data = updatedMovement;
          (selectedActivityDetails as any).status = updatedMovement.status;
        }
      }
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to update movement status"),
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const showDaysHired =
    selectedActivityDetails?.activity_sub_type === DELIVER.toUpperCase() ||
    (selectedActivityDetails?.activity_type === "OUTFLOW" &&
      selectedActivityDetails?.activity_sub_type === "TRANSFER");

  const handleSavePlannedDate = () => {
    if (!movementData || !plannedDate) return;
    const updated: MovementActivity = {
      ...movementData,
      plannedDate: new Date(plannedDate).toISOString(),
    };
    setCurrentMovementData(updated);
    if (selectedActivityDetails) {
      (selectedActivityDetails as any).raw_data = updated;
      (selectedActivityDetails as any).date = format(
        new Date(plannedDate),
        "MMM dd, yyyy",
      );
    }
    toast.success("Planned date updated");
  };

  return (
    <>
      <ReturningNoteDrawer
        movementData={movementData}
        returningNoteDetails={returningNoteDetails}
        onClose={() => {
          setReturningNoteDetails(null);
        }}
      />
      <DeliveryNoteDrawer
        movementData={movementData}
        deliveryNoteDetails={deliveryNoteDetails}
        onClose={() => {
          setDeliveryNoteDetails(null);
        }}
      />
      <SellNoteDrawer
        movementData={movementData}
        sellNoteDetails={sellNoteDetails}
        onClose={() => {
          setSellNoteDetails(null);
        }}
      />
      <TransferDetailsDrawer
        movementData={movementData}
        transferDetails={transferDetails}
        onClose={() => {
          setTransferDetails(null);
        }}
      />
      <WarehouseTransferDetailsDrawer
        movementData={movementData}
        warehouseTransferDetails={warehouseTransferDetails}
        onClose={() => {
          setWarehouseTransferDetails(null);
        }}
      />
      <DetailsOfReceivedDrawer
        movementData={movementData}
        detailsOfReceived={detailsOfReceived}
        onClose={() => {
          setDetailsOfReceived(null);
        }}
      />
      <LostItemsDetailsDrawer
        movementData={movementData}
        lostItemsDetails={lostItemsDetails}
        onClose={() => {
          setLostItemsDetails(null);
        }}
      />
      <UploadDocumentModal
        open={showUploadDocument && !isCancelledMovement}
        movementId={movementData?.id || ""}
        onClose={() => {
          setShowUploadDocument(false);
        }}
        onSuccess={async () => {
          // Refresh the movement data after successful upload
          if (movementData?.id) {
            try {
              // Fetch updated movement data
              const updatedMovement = await getMovementActivity(
                movementData.id,
              );
              setCurrentMovementData(updatedMovement);

              // Update selectedActivityDetails with new data
              if (selectedActivityDetails) {
                (selectedActivityDetails as any).raw_data = updatedMovement;
              }

              // Refresh parent component data if callback provided
              if (onRefresh) {
                await onRefresh();
              }
            } catch (error) {
              toast.error(
                getBackendErrorMessage(
                  error,
                  "Failed to refresh movement data",
                ),
              );
            }
          }
        }}
      />
      <InvoiceDrawer
        selectedInvoiceDetails={selectedOneOffInvoice}
        invoiceDetails={selectedOneOffInvoice}
        onClose={() => {
          setSelectedOneOffInvoice(null);
        }}
      />
      <SidePanel
        open={!!selectedActivityDetails}
        onClose={onClose}
        width="w-[900px]"
        layer="base"
      >
        {selectedActivityDetails && (
          <div className="relative h-full flex flex-col">
            {isMobile ? (
              <PanelHeader
                title={`${selectedActivityDetails?.source} → ${selectedActivityDetails?.destination}`}
                subtitle={
                  isLoadingMovementDetails
                    ? "Loading details..."
                    : movementData?.createdAt
                      ? `Initiated ${format(new Date(movementData.createdAt), "MMM dd, yyyy, hh:mm a")}`
                      : undefined
                }
                onClose={onClose}
                actions={[
                  !isMovementTerminal && {
                    label: isUpdatingStatus
                      ? "Updating..."
                      : getNextStageButtonLabel(
                          getNextStatus(movementData?.status || ""),
                        ),
                    onClick: handleMoveToNextStage,
                    disabled:
                      isLoadingMovementDetails ||
                      isUpdatingStatus ||
                      !getNextStatus(movementData?.status || ""),
                    variant: "default",
                    pinned: true,
                  },
                  canCancelMovement && {
                    label: "Cancel movement",
                    icon: X,
                    onClick: () => setCancelConfirmOpen(true),
                    disabled: isCancellingMovement || isUpdatingStatus,
                    destructive: true,
                  },
                ]}
              />
            ) : (
              <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
                <div className="flex items-start gap-3">
                  <h2 className="text-lg font-semibold text-foreground font-jakarta mr-auto">
                    Movement from {selectedActivityDetails?.source} to{" "}
                    {selectedActivityDetails?.destination}
                  </h2>
                  {isLoadingMovementDetails && (
                    <span className="text-sm text-gray-500 self-center">
                      Loading details...
                    </span>
                  )}
                  {!isMovementTerminal && (
                    <Button
                      className="text-nowrap"
                      onClick={handleMoveToNextStage}
                      disabled={
                        isLoadingMovementDetails ||
                        isUpdatingStatus ||
                        !getNextStatus(movementData?.status || "")
                      }
                    >
                      {isUpdatingStatus
                        ? "Updating..."
                        : getNextStageButtonLabel(
                            getNextStatus(movementData?.status || ""),
                          )}
                    </Button>
                  )}
                  {canCancelMovement && (
                    <Button
                      variant="outline"
                      className="text-nowrap border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      onClick={() => setCancelConfirmOpen(true)}
                      disabled={isCancellingMovement || isUpdatingStatus}
                    >
                      Cancel movement
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {movementData?.createdAt
                    ? `Initiated: ${format(new Date(movementData.createdAt), "MMMM dd, yyyy 'at' hh:mm a")}`
                    : "Initiated: -"}
                </p>
              </div>
            )}
            <div
              className={
                isMobile
                  ? "min-h-0 flex-1 overflow-y-auto overscroll-contain"
                  : "flex-grow overflow-hidden"
              }
            >
              <div className={isMobile ? "p-4" : "p-6"}>
                <ActivityStages stages={stages} currentStage={currentStage} />
              </div>
              <BodyScroll isMobile={isMobile}>
                <div
                  className={
                    isMobile
                      ? "w-full px-4 space-y-4 pb-8"
                      : "w-full px-6 space-y-6 pb-40"
                  }
                >
                  <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                    <div className="bg-gray-50 px-4 py-3 rounded-t-2xl">
                      {isMobile ? (
                        <div className="-mx-4 -mt-3">
                          <div className="px-4 py-3">
                            <Badge variant="onhold" className="rounded-full">
                              {selectedActivityDetails?.activity_sub_type}
                            </Badge>
                          </div>
                          <div className="divide-y divide-gray-100 border-t border-gray-100 bg-white">
                            <MobileDetailRow
                              label="Initiator"
                              value={selectedActivityDetails?.initiator}
                            />
                            <MobileDetailRow
                              label="Source"
                              value={selectedActivityDetails?.source}
                            />
                            <MobileDetailRow
                              label="Destination"
                              value={selectedActivityDetails?.destination}
                            />
                            {showDaysHired && (
                              <MobileDetailRow
                                label="Days hired"
                                value={
                                  movementData?.daysHired ??
                                  movementData?.days_hired ??
                                  "—"
                                }
                              />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <Badge variant="onhold" className="rounded-full">
                            {selectedActivityDetails?.activity_sub_type}
                          </Badge>
                          <div>
                            <h6 className="font-semibold text-base">
                              Initiator
                            </h6>
                            <p className="font-normal text-base">
                              {selectedActivityDetails?.initiator}
                            </p>
                          </div>
                          <div>
                            <h6 className="font-semibold text-base">Source</h6>
                            <p className="font-normal text-base">
                              {selectedActivityDetails?.source}
                            </p>
                          </div>
                          <div>
                            <h6 className="font-semibold text-base">
                              Destination
                            </h6>
                            <p className="font-normal text-base">
                              {selectedActivityDetails?.destination}
                            </p>
                          </div>
                          {showDaysHired && (
                            <div>
                              <h6 className="font-semibold text-base">
                                Days hired
                              </h6>
                              <p className="font-normal text-base">
                                {movementData?.daysHired ??
                                  movementData?.days_hired ??
                                  "—"}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      <Accordion
                        type="single"
                        collapsible
                        className={cn(
                          "border-t border-gray-100 w-full",
                          isMobile ? "px-0" : "px-4",
                        )}
                      >
                        <AccordionItem
                          value="project-type"
                          className="border-none"
                        >
                          <AccordionTrigger className="px-0 py-3 hover:no-underline">
                            <h6 className="font-semibold text-base">
                              Project Type
                            </h6>
                          </AccordionTrigger>
                          <AccordionContent className="px-0 pb-3 space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <SelectInput
                                value={projectType || "__none__"}
                                options={projectTypeOptions}
                                placeholder="Project type"
                                onChange={(v) => setProjectType(v)}
                                className={
                                  isMobile ? "w-full" : "min-w-[300px]"
                                }
                                disabled={isMovementTerminal}
                              />
                              <Button
                                size="sm"
                                disabled={
                                  isUpdatingProjectType || isMovementTerminal
                                }
                                onClick={async () => {
                                  if (!movementData?.id) return;
                                  setIsUpdatingProjectType(true);
                                  try {
                                    const valueToSend =
                                      projectType === "__none__" || !projectType
                                        ? undefined
                                        : projectType;
                                    const updated = await updateMovement(
                                      movementData.id,
                                      {
                                        projectType: valueToSend ?? null,
                                      },
                                    );
                                    setCurrentMovementData(updated);
                                    if (selectedActivityDetails)
                                      (
                                        selectedActivityDetails as any
                                      ).raw_data = updated;
                                    if (onRefresh) await onRefresh();
                                    setProjectType(
                                      updated.projectType?.trim()
                                        ? updated.projectType
                                        : "__none__",
                                    );
                                    toast.success("Project type updated");
                                  } catch (error) {
                                    toast.error(
                                      getBackendErrorMessage(
                                        error,
                                        "Failed to update project type",
                                      ),
                                    );
                                  } finally {
                                    setIsUpdatingProjectType(false);
                                  }
                                }}
                              >
                                {isUpdatingProjectType ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem
                          value="planned-date"
                          className="border-none"
                        >
                          <AccordionTrigger className="px-0 py-3 hover:no-underline">
                            <h6 className="font-semibold text-base">
                              Planned Date
                            </h6>
                          </AccordionTrigger>
                          <AccordionContent className="px-0 pb-3 space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Input
                                id="drawer-plannedDate"
                                type="date"
                                className="h-9 max-w-[200px]"
                                value={plannedDate}
                                onChange={(e) => setPlannedDate(e.target.value)}
                              />
                              <Button
                                size="sm"
                                disabled={
                                  !plannedDate ||
                                  plannedDate ===
                                    toDateInputValue(movementData?.plannedDate)
                                }
                                onClick={handleSavePlannedDate}
                              >
                                Save
                              </Button>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                        <AccordionItem
                          value="linked-invoices"
                          className="border-none"
                        >
                          <AccordionTrigger className="px-0 py-3 hover:no-underline">
                            <h6 className="font-semibold text-base">
                              Linked Invoices
                              {linkedInvoices.length > 0 && (
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                  ({linkedInvoices.length})
                                </span>
                              )}
                            </h6>
                          </AccordionTrigger>
                          <AccordionContent className="px-0 pb-3 space-y-3">
                            {linkedInvoices.length > 0 ? (
                              <div className="space-y-3">
                                {linkedInvoices.map((invoice) => (
                                  <div
                                    key={invoice.id}
                                    className="border border-gray-200 rounded-lg p-3 space-y-2 bg-white"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="font-medium text-sm">
                                          {invoice.reference}
                                        </p>
                                        <p className="text-sm text-gray-500 truncate">
                                          {invoice.customer?.fullName ?? "—"}
                                        </p>
                                      </div>
                                      <Badge
                                        variant={getBadgeVariant(
                                          invoice.status,
                                        )}
                                        className="shrink-0"
                                      >
                                        {invoice.status}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-700">
                                      {formatInvoiceTotal(invoice)}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap pt-1">
                                      <Button
                                        size="sm"
                                        variant="outlinePrimary"
                                        onClick={() =>
                                          handleViewInvoice(invoice)
                                        }
                                      >
                                        View details
                                      </Button>
                                      {!isCancelledMovement && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                                          disabled={isSavingInvoices}
                                          onClick={() =>
                                            setUnlinkInvoiceTarget({
                                              type: "one",
                                              id: invoice.id,
                                              reference: invoice.reference,
                                            })
                                          }
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {!isCancelledMovement && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                                    disabled={isSavingInvoices}
                                    onClick={() =>
                                      setUnlinkInvoiceTarget({ type: "all" })
                                    }
                                  >
                                    Unlink all
                                  </Button>
                                )}
                              </div>
                            ) : isCancelledMovement ? (
                              <p className="text-sm text-gray-500">
                                No invoices linked.
                              </p>
                            ) : null}

                            {!isCancelledMovement && (
                              <div className="space-y-3 pt-1 border-t border-gray-100">
                                <p className="text-sm font-medium text-gray-700">
                                  Add invoice
                                </p>
                                <div
                                  className={
                                    isMobile
                                      ? "w-full"
                                      : "min-w-[280px] max-w-md"
                                  }
                                >
                                  <SearchableSelectInput
                                    options={availableInvoiceOptions}
                                    value={linkInvoiceId}
                                    defaultLabel={
                                      isLoadingInvoices
                                        ? "Loading invoices..."
                                        : availableInvoiceOptions.length === 0
                                          ? "No more invoices to add"
                                          : "Select an invoice"
                                    }
                                    onSelect={setLinkInvoiceId}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  disabled={
                                    isSavingInvoices ||
                                    isLoadingInvoices ||
                                    !linkInvoiceId ||
                                    !movementData?.id ||
                                    availableInvoiceOptions.length === 0
                                  }
                                  onClick={() => void handleLinkInvoice()}
                                >
                                  {isSavingInvoices
                                    ? "Saving..."
                                    : "Link invoice"}
                                </Button>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                        {canExtendDaysHired && !isCancelledMovement && (
                          <AccordionItem
                            value="extend-days-hired"
                            className="border-none"
                          >
                            <AccordionTrigger className="px-0 py-3 hover:no-underline">
                              <h6 className="font-semibold text-base">
                                Extend Days Hired
                              </h6>
                            </AccordionTrigger>
                            <AccordionContent className="px-0 pb-3 space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="drawer-extendDateFrom"
                                    className="text-sm text-gray-600"
                                  >
                                    Date From
                                  </Label>
                                  <Input
                                    id="drawer-extendDateFrom"
                                    type="date"
                                    className="h-9"
                                    value={extendDateFrom}
                                    onChange={(e) =>
                                      setExtendDateFrom(e.target.value)
                                    }
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="drawer-extendDateTo"
                                    className="text-sm text-gray-600"
                                  >
                                    Date To
                                  </Label>
                                  <Input
                                    id="drawer-extendDateTo"
                                    type="date"
                                    className="h-9"
                                    value={extendDateTo}
                                    min={extendDateFrom || undefined}
                                    onChange={(e) =>
                                      setExtendDateTo(e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                              <div className="space-y-1.5">
                                <Label
                                  htmlFor="drawer-extendDaysNotes"
                                  className="text-sm text-gray-600"
                                >
                                  Notes (Optional)
                                </Label>
                                <Input
                                  id="drawer-extendDaysNotes"
                                  className="h-9"
                                  value={extendDaysNotes}
                                  onChange={(e) =>
                                    setExtendDaysNotes(e.target.value)
                                  }
                                  placeholder="Reason for extension"
                                />
                              </div>
                              {(() => {
                                const calculatedDays = inclusiveDaysBetween(
                                  extendDateFrom,
                                  extendDateTo,
                                );
                                if (calculatedDays == null) return null;
                                const currentDays = Number(
                                  movementData?.daysHired ??
                                    movementData?.days_hired ??
                                    0,
                                );
                                return (
                                  <p className="text-sm text-gray-600">
                                    Calculated days (inclusive):{" "}
                                    <span className="font-semibold text-gray-900">
                                      {calculatedDays}
                                    </span>
                                    {" · "}
                                    New days hired:{" "}
                                    <span className="font-semibold text-gray-900">
                                      {(Number.isFinite(currentDays)
                                        ? currentDays
                                        : 0) + calculatedDays}
                                    </span>
                                  </p>
                                );
                              })()}
                              <Button
                                size="sm"
                                className="mt-2"
                                disabled={
                                  isExtendingDaysHired ||
                                  !movementData?.id ||
                                  !extendDateFrom ||
                                  !extendDateTo ||
                                  inclusiveDaysBetween(
                                    extendDateFrom,
                                    extendDateTo,
                                  ) == null
                                }
                                onClick={async () => {
                                  if (!movementData?.id) return;
                                  const calculatedDays = inclusiveDaysBetween(
                                    extendDateFrom,
                                    extendDateTo,
                                  );
                                  if (
                                    calculatedDays == null ||
                                    calculatedDays < 1
                                  ) {
                                    toast.error(
                                      "Date To must be on or after Date From",
                                    );
                                    return;
                                  }
                                  try {
                                    setIsExtendingDaysHired(true);
                                    const updated =
                                      await extendMovementDaysHired(
                                        movementData.id,
                                        {
                                          dateFrom: extendDateFrom,
                                          dateTo: extendDateTo,
                                          notes:
                                            extendDaysNotes.trim() || undefined,
                                        },
                                      );
                                    setCurrentMovementData(updated);
                                    if (selectedActivityDetails) {
                                      (
                                        selectedActivityDetails as any
                                      ).raw_data = updated;
                                    }
                                    if (onRefresh) await onRefresh();
                                    setExtendDateFrom(
                                      getDefaultExtensionDateFrom(updated),
                                    );
                                    setExtendDateTo("");
                                    setExtendDaysNotes("");
                                    toast.success(
                                      `Days hired extended by ${calculatedDays} day${calculatedDays === 1 ? "" : "s"}`,
                                    );
                                  } catch (error) {
                                    toast.error(
                                      getBackendErrorMessage(
                                        error,
                                        "Failed to extend days hired",
                                      ),
                                    );
                                  } finally {
                                    setIsExtendingDaysHired(false);
                                  }
                                }}
                              >
                                {isExtendingDaysHired
                                  ? "Extending..."
                                  : "Extend"}
                              </Button>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                        <AccordionItem
                          value="driver-vehicle"
                          className="border-none"
                        >
                          <AccordionTrigger className="px-0 py-3 hover:no-underline">
                            <h6 className="font-semibold text-base">
                              Driver & Vehicle
                            </h6>
                          </AccordionTrigger>
                          <AccordionContent className="px-0 pb-3 space-y-3">
                            {isMovementTerminal ? (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-base">
                                <div>
                                  <p className="text-gray-500 text-sm">
                                    Driver Name
                                  </p>
                                  <p className="font-normal">
                                    {movementData?.driverName ??
                                      movementData?.driver_name ??
                                      "—"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500 text-sm">
                                    Driver Phone No
                                  </p>
                                  <p className="font-normal">
                                    {movementData?.driverPhoneNumber ??
                                      movementData?.driver_phone_number ??
                                      "—"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500 text-sm">
                                    Vehicle No.
                                  </p>
                                  <p className="font-normal">
                                    {movementData?.vehicleNumber ??
                                      movementData?.vehicle_number ??
                                      "—"}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="drawer-driverName"
                                    className="text-sm text-gray-600"
                                  >
                                    Driver Name
                                  </Label>
                                  <Input
                                    id="drawer-driverName"
                                    className="h-9"
                                    value={driverName}
                                    onChange={(e) =>
                                      setDriverName(e.target.value)
                                    }
                                    placeholder="Driver name"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="drawer-driverPhone"
                                    className="text-sm text-gray-600"
                                  >
                                    Driver Phone No
                                  </Label>
                                  <Input
                                    id="drawer-driverPhone"
                                    className="h-9"
                                    value={driverPhoneNumber}
                                    onChange={(e) =>
                                      setDriverPhoneNumber(e.target.value)
                                    }
                                    placeholder="Phone number"
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="drawer-vehicleNumber"
                                    className="text-sm text-gray-600"
                                  >
                                    Vehicle No.
                                  </Label>
                                  <Input
                                    id="drawer-vehicleNumber"
                                    className="h-9"
                                    value={vehicleNumber}
                                    onChange={(e) =>
                                      setVehicleNumber(e.target.value)
                                    }
                                    placeholder="Vehicle number"
                                  />
                                </div>
                              </div>
                            )}
                            {!isMovementTerminal && (
                              <Button
                                size="sm"
                                className="mt-2"
                                disabled={
                                  isUpdatingDriverVehicle ||
                                  (driverName.trim() ===
                                    (movementData?.driverName ??
                                      movementData?.driver_name ??
                                      "") &&
                                    driverPhoneNumber.trim() ===
                                      (movementData?.driverPhoneNumber ??
                                        movementData?.driver_phone_number ??
                                        "") &&
                                    vehicleNumber.trim() ===
                                      (movementData?.vehicleNumber ??
                                        movementData?.vehicle_number ??
                                        "") &&
                                    (!(
                                      isReturnSubType ||
                                      isPurchaseSubType ||
                                      isTransferOutflowSubType
                                    ) ||
                                      (receivedBy.trim() ===
                                        (movementData?.receivedBy ??
                                          movementData?.received_by ??
                                          "") &&
                                        (!isApprovedBySubtype ||
                                          approvedBy.trim() ===
                                            (movementData?.approvedBy ??
                                              movementData?.approved_by ??
                                              "")) &&
                                        (!isReturnSubType ||
                                          (returnedBy.trim() ===
                                            (movementData?.returnedBy ??
                                              movementData?.returned_by ??
                                              "") &&
                                            returnedDate ===
                                              ((movementData?.returnedDate ??
                                              movementData?.returned_date)
                                                ? new Date(
                                                    movementData.returnedDate ??
                                                      movementData?.returned_date ??
                                                      "",
                                                  )
                                                    .toISOString()
                                                    .slice(0, 10)
                                                : ""))))))
                                }
                                onClick={async () => {
                                  if (!movementData?.id) return;
                                  setIsUpdatingDriverVehicle(true);
                                  try {
                                    const isReturn = isReturnSubType;
                                    const isPurchase = isPurchaseSubType;
                                    const isTransferOutflow =
                                      isTransferOutflowSubType;
                                    const updated = await updateMovement(
                                      movementData.id,
                                      {
                                        driverName:
                                          driverName.trim() || undefined,
                                        driverPhoneNumber:
                                          driverPhoneNumber.trim() || undefined,
                                        vehicleNumber:
                                          vehicleNumber.trim() || undefined,
                                        ...(isReturn && {
                                          returnedBy:
                                            returnedBy.trim() || undefined,
                                          receivedBy:
                                            receivedBy.trim() || undefined,
                                          returnedDate:
                                            returnedDate || undefined,
                                        }),
                                        ...(isPurchase &&
                                          !isReturn && {
                                            receivedBy:
                                              receivedBy.trim() || undefined,
                                            approvedBy:
                                              approvedBy.trim() || undefined,
                                          }),
                                        ...(isTransferOutflow &&
                                          !isReturn &&
                                          !isPurchase && {
                                            returnedBy:
                                              returnedBy.trim() || undefined,
                                            receivedBy:
                                              receivedBy.trim() || undefined,
                                            approvedBy:
                                              approvedBy.trim() || undefined,
                                            returnedDate:
                                              returnedDate || undefined,
                                          }),
                                      },
                                    );
                                    setCurrentMovementData(updated);
                                    if (selectedActivityDetails)
                                      (
                                        selectedActivityDetails as any
                                      ).raw_data = updated;
                                    if (onRefresh) await onRefresh();
                                    toast.success(
                                      isReturnOrTransferOutflow
                                        ? "Details updated"
                                        : "Driver and vehicle details updated",
                                    );
                                  } catch (error) {
                                    toast.error(
                                      getBackendErrorMessage(
                                        error,
                                        "Failed to update details",
                                      ),
                                    );
                                  } finally {
                                    setIsUpdatingDriverVehicle(false);
                                  }
                                }}
                              >
                                {isUpdatingDriverVehicle ? "Saving..." : "Save"}
                              </Button>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                        {(isReturnSubType ||
                          isPurchaseSubType ||
                          isTransferOutflowSubType ||
                          isTransferInflowSubType) && (
                          <AccordionItem
                            value="returned-received"
                            className="border-none"
                          >
                            <AccordionTrigger className="px-0 py-3 hover:no-underline">
                              <h6 className="font-semibold text-base">
                                {isTransferInflowSubType
                                  ? "Sending Warehouse, Receiving Warehouse & Authorized By"
                                  : isPurchaseSubType
                                    ? "Received By & Approved By"
                                    : isTransferOutflowSubType
                                      ? "Returned By, Received By & Approved By"
                                      : "Returned By & Received By"}
                              </h6>
                            </AccordionTrigger>
                            <AccordionContent className="px-0 pb-3 space-y-3">
                              {isMovementTerminal ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-base">
                                  {(isReturnOrTransferOutflow ||
                                    isTransferInflowSubType) && (
                                    <div>
                                      <p className="text-gray-500 text-sm">
                                        {isTransferInflowSubType
                                          ? "Sending Warehouse"
                                          : "Returned By"}
                                      </p>
                                      <p className="font-normal">
                                        {movementData?.returnedBy ??
                                          movementData?.returned_by ??
                                          "—"}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-gray-500 text-sm">
                                      {isTransferInflowSubType
                                        ? "Receiving Warehouse"
                                        : "Received By"}
                                    </p>
                                    <p className="font-normal">
                                      {movementData?.receivedBy ??
                                        movementData?.received_by ??
                                        "—"}
                                    </p>
                                  </div>
                                  {isApprovedBySubtype && (
                                    <div>
                                      <p className="text-gray-500 text-sm">
                                        {isTransferInflowSubType
                                          ? "Authorized By"
                                          : "Approved By"}
                                      </p>
                                      <p className="font-normal">
                                        {movementData?.approvedBy ??
                                          movementData?.approved_by ??
                                          "—"}
                                      </p>
                                    </div>
                                  )}
                                  {isReturnOrTransferOutflow && (
                                    <div className="sm:col-span-2">
                                      <p className="text-gray-500 text-sm">
                                        Returned Date
                                      </p>
                                      <p className="font-normal">
                                        {(movementData?.returnedDate ??
                                        movementData?.returned_date)
                                          ? format(
                                              new Date(
                                                movementData.returnedDate ??
                                                  movementData?.returned_date ??
                                                  "",
                                              ),
                                              "MMM dd, yyyy",
                                            )
                                          : "—"}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {(isReturnOrTransferOutflow ||
                                      isTransferInflowSubType) && (
                                      <div className="space-y-1.5">
                                        <Label
                                          htmlFor="drawer-returnedBy"
                                          className="text-sm text-gray-600"
                                        >
                                          {isTransferInflowSubType
                                            ? "Sending Warehouse"
                                            : "Returned By"}
                                        </Label>
                                        <Input
                                          id="drawer-returnedBy"
                                          className="h-9"
                                          value={returnedBy}
                                          onChange={(e) =>
                                            setReturnedBy(e.target.value)
                                          }
                                          placeholder={
                                            isTransferInflowSubType
                                              ? "Sending warehouse"
                                              : "Returned by"
                                          }
                                        />
                                      </div>
                                    )}
                                    <div className="space-y-1.5">
                                      <Label
                                        htmlFor="drawer-receivedBy"
                                        className="text-sm text-gray-600"
                                      >
                                        {isTransferInflowSubType
                                          ? "Receiving Warehouse"
                                          : "Received By"}
                                      </Label>
                                      <Input
                                        id="drawer-receivedBy"
                                        className="h-9"
                                        value={receivedBy}
                                        onChange={(e) =>
                                          setReceivedBy(e.target.value)
                                        }
                                        placeholder={
                                          isTransferInflowSubType
                                            ? "Receiving warehouse"
                                            : "Received by"
                                        }
                                      />
                                    </div>
                                    {isApprovedBySubtype && (
                                      <div className="space-y-1.5">
                                        <Label
                                          htmlFor="drawer-approvedBy"
                                          className="text-sm text-gray-600"
                                        >
                                          {isTransferInflowSubType
                                            ? "Authorized By"
                                            : "Approved By"}
                                        </Label>
                                        <Input
                                          id="drawer-approvedBy"
                                          className="h-9"
                                          value={approvedBy}
                                          onChange={(e) =>
                                            setApprovedBy(e.target.value)
                                          }
                                          placeholder={
                                            isTransferInflowSubType
                                              ? "Authorized by"
                                              : "Approved by"
                                          }
                                        />
                                      </div>
                                    )}
                                    {isReturnOrTransferOutflow && (
                                      <div className="space-y-1.5 sm:col-span-2">
                                        <Label
                                          htmlFor="drawer-returnedDate"
                                          className="text-sm text-gray-600"
                                        >
                                          Returned Date
                                        </Label>
                                        <Input
                                          id="drawer-returnedDate"
                                          type="date"
                                          className="h-9 max-w-[200px]"
                                          value={returnedDate}
                                          onChange={(e) =>
                                            setReturnedDate(e.target.value)
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    className="mt-2"
                                    disabled={
                                      isUpdatingDriverVehicle ||
                                      (driverName.trim() ===
                                        (movementData?.driverName ??
                                          movementData?.driver_name ??
                                          "") &&
                                        driverPhoneNumber.trim() ===
                                          (movementData?.driverPhoneNumber ??
                                            movementData?.driver_phone_number ??
                                            "") &&
                                        vehicleNumber.trim() ===
                                          (movementData?.vehicleNumber ??
                                            movementData?.vehicle_number ??
                                            "") &&
                                        receivedBy.trim() ===
                                          (movementData?.receivedBy ??
                                            movementData?.received_by ??
                                            "") &&
                                        (!isApprovedBySubtype ||
                                          approvedBy.trim() ===
                                            (movementData?.approvedBy ??
                                              movementData?.approved_by ??
                                              "")) &&
                                        (!isReturnOrTransferOutflow ||
                                          (returnedBy.trim() ===
                                            (movementData?.returnedBy ??
                                              movementData?.returned_by ??
                                              "") &&
                                            returnedDate ===
                                              ((movementData?.returnedDate ??
                                              movementData?.returned_date)
                                                ? new Date(
                                                    movementData.returnedDate ??
                                                      movementData?.returned_date ??
                                                      "",
                                                  )
                                                    .toISOString()
                                                    .slice(0, 10)
                                                : ""))))
                                    }
                                    onClick={async () => {
                                      if (!movementData?.id) return;
                                      setIsUpdatingDriverVehicle(true);
                                      try {
                                        const updated = await updateMovement(
                                          movementData.id,
                                          {
                                            driverName:
                                              driverName.trim() || undefined,
                                            driverPhoneNumber:
                                              driverPhoneNumber.trim() ||
                                              undefined,
                                            vehicleNumber:
                                              vehicleNumber.trim() || undefined,
                                            receivedBy:
                                              receivedBy.trim() || undefined,
                                            ...(isApprovedBySubtype && {
                                              approvedBy:
                                                approvedBy.trim() || undefined,
                                            }),
                                            ...(isTransferInflowSubType && {
                                              returnedBy:
                                                returnedBy.trim() || undefined,
                                            }),
                                            ...(isReturnOrTransferOutflow && {
                                              returnedBy:
                                                returnedBy.trim() || undefined,
                                              returnedDate:
                                                returnedDate || undefined,
                                            }),
                                          },
                                        );
                                        setCurrentMovementData(updated);
                                        if (selectedActivityDetails)
                                          (
                                            selectedActivityDetails as any
                                          ).raw_data = updated;
                                        if (onRefresh) await onRefresh();
                                        toast.success("Details updated");
                                      } catch (error) {
                                        toast.error(
                                          getBackendErrorMessage(
                                            error,
                                            "Failed to update details",
                                          ),
                                        );
                                      } finally {
                                        setIsUpdatingDriverVehicle(false);
                                      }
                                    }}
                                  >
                                    {isUpdatingDriverVehicle
                                      ? "Saving..."
                                      : "Save"}
                                  </Button>
                                </>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </Accordion>
                    </div>
                    <div className="mb-4 bg-white px-4 space-y-3">
                      <div
                        className={
                          isMobile
                            ? "flex flex-col gap-2 pt-4 [&>button]:w-full"
                            : "flex items-center gap-3 pt-4 flex-wrap"
                        }
                      >
                        {selectedActivityDetails?.activity_sub_type ===
                          RETURN.toUpperCase() && (
                          <Button
                            variant="outlinePrimary"
                            onClick={() => {
                              setReturningNoteDetails({});
                            }}
                          >
                            Download Returning Note
                          </Button>
                        )}
                        {selectedActivityDetails?.activity_sub_type ===
                          DELIVER.toUpperCase() && (
                          <Button
                            variant="outlinePrimary"
                            onClick={() => {
                              setDeliveryNoteDetails({});
                            }}
                          >
                            Download Delivery Note
                          </Button>
                        )}
                        {selectedActivityDetails?.activity_sub_type ===
                          SELL.toUpperCase() && (
                          <Button
                            variant="outlinePrimary"
                            onClick={() => {
                              setSellNoteDetails({});
                            }}
                          >
                            Download Sell Note
                          </Button>
                        )}
                        {selectedActivityDetails?.activity_type === "OUTFLOW" &&
                          selectedActivityDetails?.activity_sub_type ===
                            "TRANSFER" && (
                            <Button
                              variant="outlinePrimary"
                              onClick={() => {
                                setTransferDetails({});
                              }}
                            >
                              Download Transfer Details
                            </Button>
                          )}
                        {selectedActivityDetails?.activity_type === "INFLOW" &&
                          selectedActivityDetails?.activity_sub_type ===
                            "TRANSFER" && (
                            <Button
                              variant="outlinePrimary"
                              onClick={() => {
                                setWarehouseTransferDetails({});
                              }}
                            >
                              Download Warehouse Transfer Details
                            </Button>
                          )}
                        {selectedActivityDetails?.activity_type === "INFLOW" &&
                          selectedActivityDetails?.activity_sub_type ===
                            "PURCHASE" && (
                            <Button
                              variant="outlinePrimary"
                              onClick={() => {
                                setDetailsOfReceived({});
                              }}
                            >
                              Download Details of Received
                            </Button>
                          )}
                        {selectedActivityDetails?.activity_type === "OUTFLOW" &&
                          selectedActivityDetails?.activity_sub_type ===
                            "LOST" && (
                            <Button
                              variant="outlinePrimary"
                              onClick={() => {
                                setLostItemsDetails({});
                              }}
                            >
                              Download Lost Items Note
                            </Button>
                          )}
                        <Button
                          variant="outlinePrimary"
                          disabled={isCancelledMovement}
                          onClick={() => {
                            setShowUploadDocument(true);
                          }}
                        >
                          Upload Documents
                        </Button>
                      </div>
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="items">
                          <AccordionTrigger>
                            <h4 className="text-base font-bold">Items</h4>
                          </AccordionTrigger>
                          <AccordionContent>
                            {isMobile ? (
                              <MobileItemsList
                                items={movementData?.items ?? []}
                              />
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableHeaderRow>
                                    <TableColumnHeader>
                                      Item Code
                                    </TableColumnHeader>
                                    <TableColumnHeader>
                                      Particulars
                                    </TableColumnHeader>
                                    <TableColumnHeader>
                                      Unit Weight
                                    </TableColumnHeader>
                                    <TableColumnHeader>
                                      Total Qty
                                    </TableColumnHeader>
                                    <TableColumnHeader>
                                      Good Qty
                                    </TableColumnHeader>
                                    <TableColumnHeader>
                                      Damaged Qty
                                    </TableColumnHeader>
                                    <TableColumnHeader>
                                      Total Weight
                                    </TableColumnHeader>
                                  </TableHeaderRow>
                                </TableHeader>
                                <TableBody>
                                  {movementData?.items &&
                                  movementData.items.length > 0 ? (
                                    movementData.items.map((item) => (
                                      <TableRow
                                        key={item.id}
                                        className="group hover:bg-gray-50 transition-all duration-200 border"
                                      >
                                        <TableCell>
                                          {item.inventory?.itemCode ||
                                          item.inventory?.itemCode ===
                                            "undefined"
                                            ? "---"
                                            : "---"}
                                        </TableCell>
                                        <TableCell>
                                          {[
                                            item.inventory?.description,
                                            item.inventory?.length != null &&
                                            item.inventory.length !== "" &&
                                            String(item.inventory.length) !==
                                              "undefined"
                                              ? String(item.inventory.length)
                                              : null,
                                          ]
                                            .filter(Boolean)
                                            .join(" ") || "—"}
                                        </TableCell>
                                        <TableCell>
                                          {item.inventory?.unitWeight != null &&
                                          item.inventory.unitWeight !== ""
                                            ? String(item.inventory.unitWeight)
                                            : "—"}
                                        </TableCell>
                                        <TableCell>
                                          {item.totalQuantity}
                                        </TableCell>
                                        <TableCell>
                                          {item.goodQuantity}
                                        </TableCell>
                                        <TableCell>
                                          {item.damagedQuantity}
                                        </TableCell>
                                        <TableCell>
                                          {(() => {
                                            const uw =
                                              item.inventory?.unitWeight;
                                            const qty = item.totalQuantity ?? 0;
                                            if (uw == null || uw === "" || !qty)
                                              return "—";
                                            const n = Number(uw);
                                            return Number.isFinite(n)
                                              ? (n * qty).toLocaleString(
                                                  undefined,
                                                  { maximumFractionDigits: 2 },
                                                )
                                              : "—";
                                          })()}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell
                                        colSpan={8}
                                        className="text-center text-gray-500 py-4"
                                      >
                                        No items found
                                      </TableCell>
                                    </TableRow>
                                  )}
                                  {movementData?.items &&
                                    movementData.items.length > 0 &&
                                    (() => {
                                      const grandTotal =
                                        movementData.items.reduce(
                                          (sum, item) => {
                                            const uw =
                                              item.inventory?.unitWeight;
                                            const qty = item.totalQuantity ?? 0;
                                            if (uw == null || uw === "" || !qty)
                                              return sum;
                                            const n = Number(uw);
                                            return (
                                              sum +
                                              (Number.isFinite(n) ? n * qty : 0)
                                            );
                                          },
                                          0,
                                        );
                                      return (
                                        <TableRow className="bg-gray-100 font-semibold border-t-2 border-gray-200">
                                          <TableCell
                                            colSpan={6}
                                            className="text-right"
                                          >
                                            Grand Total Weight
                                          </TableCell>
                                          <TableCell>
                                            {grandTotal > 0
                                              ? grandTotal.toLocaleString(
                                                  undefined,
                                                  {
                                                    maximumFractionDigits: 2,
                                                  },
                                                )
                                              : "—"}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })()}
                                </TableBody>
                              </Table>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="documents">
                          <AccordionTrigger>
                            <h4 className="text-base font-bold">
                              Attachment Documents
                            </h4>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div>
                              {movementData?.documents &&
                              movementData.documents.length > 0 ? (
                                movementData.documents.map((document) => (
                                  <div
                                    key={document.id}
                                    className={
                                      isMobile
                                        ? "flex items-center justify-between gap-2 py-3 border-b last:border-b-0"
                                        : "flex items-center justify-between p-4 border-b last:border-b-0"
                                    }
                                  >
                                    <div
                                      className={
                                        isMobile
                                          ? "flex min-w-0 items-center gap-3"
                                          : "flex items-center space-x-4"
                                      }
                                    >
                                      <Icons.Document
                                        className={
                                          isMobile
                                            ? "w-8 h-8 shrink-0 text-gray-400"
                                            : "w-10 h-10 text-gray-400"
                                        }
                                      />
                                      <div
                                        className={
                                          isMobile ? "min-w-0" : undefined
                                        }
                                      >
                                        <p
                                          className={
                                            isMobile
                                              ? "font-medium text-gray-900 break-all"
                                              : "font-medium text-gray-900"
                                          }
                                        >
                                          {document.fileName}
                                        </p>
                                        <p className="text-gray-500 text-sm">
                                          Uploaded{" "}
                                          {format(
                                            new Date(document.uploadedAt),
                                            "MMM dd, yyyy 'at' hh:mm a",
                                          )}
                                        </p>
                                        {document.mimeType && (
                                          <p className="text-gray-400 text-xs mt-1">
                                            {document.mimeType}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      className={
                                        isMobile
                                          ? "flex shrink-0 gap-1"
                                          : "flex space-x-2"
                                      }
                                    >
                                      {document.filePath && (
                                        <a
                                          href={document.filePath}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-2 rounded-md hover:bg-gray-100"
                                        >
                                          <Icons.Preview className="w-5 h-5 text-gray-600" />
                                        </a>
                                      )}
                                      {document.filePath && (
                                        <a
                                          href={document.filePath}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-2 rounded-md hover:bg-gray-100"
                                        >
                                          <Download className="w-5 h-5 text-gray-600" />
                                        </a>
                                      )}
                                      {!document.filePath && (
                                        <span className="text-gray-400 text-xs">
                                          No file available
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center text-gray-500 py-8">
                                  No documents attached
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="movement history">
                          <AccordionTrigger>
                            <h4 className="text-base font-bold">
                              Status History
                            </h4>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-6">
                              {movementData?.statusHistory &&
                              movementData.statusHistory.length > 0 ? (
                                movementData.statusHistory.map(
                                  (historyItem) => (
                                    <div
                                      key={historyItem.id}
                                      className="border border-gray-200 rounded-2xl transition-all duration-200"
                                    >
                                      <div
                                        className={
                                          isMobile
                                            ? "bg-gray-50 px-4 py-3 rounded-t-2xl flex flex-wrap items-center justify-between gap-2"
                                            : "bg-gray-50 px-4 py-3 rounded-t-2xl flex items-center justify-between"
                                        }
                                      >
                                        <Badge
                                          variant={getBadgeVariant(
                                            historyItem.newStatus,
                                          )}
                                          className="rounded-full"
                                        >
                                          {historyItem.newStatus}
                                        </Badge>
                                        <span className="text-sm text-gray-500">
                                          {format(
                                            new Date(historyItem.changedAt),
                                            "MMM dd, yyyy 'at' hh:mm a",
                                          )}
                                        </span>
                                      </div>
                                      <div className="mb-4 bg-white px-4">
                                        <div className="space-y-3 mt-4">
                                          <h6 className="font-bold text-base">
                                            Status Change Details
                                          </h6>
                                          <p>
                                            <span className="font-semibold text-sm">
                                              Previous Status{" "}
                                            </span>
                                            <span className="font-normal text-sm">
                                              : {historyItem.previousStatus}
                                            </span>
                                          </p>
                                          <p>
                                            <span className="font-semibold text-sm">
                                              New Status{" "}
                                            </span>
                                            <span className="font-normal text-sm">
                                              : {historyItem.newStatus}
                                            </span>
                                          </p>
                                          <p>
                                            <span className="font-semibold text-sm">
                                              Changed By{" "}
                                            </span>
                                            <span className="font-normal text-sm">
                                              :{" "}
                                              {historyItem.changedByUser
                                                ?.fullName || "-"}
                                            </span>
                                          </p>
                                          {historyItem.notes && (
                                            <p>
                                              <span className="font-semibold text-sm">
                                                Notes{" "}
                                              </span>
                                              <span className="font-normal text-sm">
                                                : {historyItem.notes}
                                              </span>
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )
                              ) : (
                                <div className="text-center text-gray-500 py-8">
                                  No status history available
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      {(canExtendDaysHired ||
                        (movementData?.daysHiredExtensions &&
                          movementData.daysHiredExtensions.length > 0)) && (
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="days-hired-extension-history">
                            <AccordionTrigger>
                              <h4 className="text-base font-bold">
                                Extension History
                              </h4>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-6">
                                {movementData?.daysHiredExtensions &&
                                movementData.daysHiredExtensions.length > 0 ? (
                                  [...movementData.daysHiredExtensions]
                                    .sort(
                                      (a, b) =>
                                        new Date(a.createdAt).getTime() -
                                        new Date(b.createdAt).getTime(),
                                    )
                                    .map((extension) => (
                                      <div
                                        key={extension.id}
                                        className="border border-gray-200 rounded-2xl transition-all duration-200"
                                      >
                                        <div
                                          className={cn(
                                            "bg-gray-50 px-4 py-3 rounded-t-2xl flex items-center justify-between gap-3",
                                            isMobile && "flex-wrap gap-2",
                                          )}
                                        >
                                          <Badge
                                            variant="onhold"
                                            className="rounded-full"
                                          >
                                            +{extension.daysAdded} day
                                            {extension.daysAdded === 1
                                              ? ""
                                              : "s"}
                                          </Badge>
                                          <span className="text-sm text-gray-500">
                                            {format(
                                              new Date(extension.createdAt),
                                              "MMM dd, yyyy 'at' hh:mm a",
                                            )}
                                          </span>
                                        </div>
                                        <div className="mb-4 bg-white px-4">
                                          <div className="space-y-3 mt-4">
                                            <h6 className="font-bold text-base">
                                              Extension Details
                                            </h6>
                                            <p>
                                              <span className="font-semibold text-sm">
                                                Date From{" "}
                                              </span>
                                              <span className="font-normal text-sm">
                                                :{" "}
                                                {format(
                                                  new Date(extension.dateFrom),
                                                  "MMM dd, yyyy",
                                                )}
                                              </span>
                                            </p>
                                            <p>
                                              <span className="font-semibold text-sm">
                                                Date To{" "}
                                              </span>
                                              <span className="font-normal text-sm">
                                                :{" "}
                                                {format(
                                                  new Date(extension.dateTo),
                                                  "MMM dd, yyyy",
                                                )}
                                              </span>
                                            </p>
                                            <p>
                                              <span className="font-semibold text-sm">
                                                Days Added{" "}
                                              </span>
                                              <span className="font-normal text-sm">
                                                : {extension.daysAdded}
                                              </span>
                                            </p>
                                            <p>
                                              <span className="font-semibold text-sm">
                                                Days Hired{" "}
                                              </span>
                                              <span className="font-normal text-sm">
                                                : {extension.previousDaysHired}{" "}
                                                → {extension.newDaysHired}
                                              </span>
                                            </p>
                                            <p>
                                              <span className="font-semibold text-sm">
                                                Extended By{" "}
                                              </span>
                                              <span className="font-normal text-sm">
                                                :{" "}
                                                {extension.extendedByUser
                                                  ?.fullName || "-"}
                                              </span>
                                            </p>
                                            {extension.notes && (
                                              <p>
                                                <span className="font-semibold text-sm">
                                                  Notes{" "}
                                                </span>
                                                <span className="font-normal text-sm">
                                                  : {extension.notes}
                                                </span>
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                ) : (
                                  <div className="text-center text-gray-500 py-8">
                                    No extension history available
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                    <div className="bg-gray-50 px-4 py-3 rounded-t-2xl">
                      <Badge variant="onhold" className="rounded-full">
                        Movement Details
                      </Badge>
                    </div>
                    <div className="mb-4 bg-white px-4 space-y-3">
                      <h6 className="font-bold text-base mt-4">
                        Movement Details
                      </h6>
                      <p>
                        <span className="font-semibold text-sm">
                          Movement Type{" "}
                        </span>
                        <span className="font-normal text-sm">
                          : {selectedActivityDetails?.activity_type}
                        </span>
                      </p>
                      <p>
                        <span className="font-semibold text-sm">
                          Movement Sub Type{" "}
                        </span>
                        <span className="font-normal text-sm">
                          : {selectedActivityDetails?.activity_sub_type}
                        </span>
                      </p>
                      <p>
                        <span className="font-semibold text-sm">Source </span>
                        <span className="font-normal text-sm">
                          : {selectedActivityDetails?.source}
                        </span>
                      </p>
                      <p>
                        <span className="font-semibold text-sm">
                          Destination{" "}
                        </span>
                        <span className="font-normal text-sm">
                          : {selectedActivityDetails?.destination}
                        </span>
                      </p>
                      <p>
                        <span className="font-semibold text-sm">
                          Planned Date{" "}
                        </span>
                        <span className="font-normal text-sm">
                          :{" "}
                          {movementData?.plannedDate
                            ? format(
                                new Date(movementData.plannedDate),
                                "MMMM dd, yyyy",
                              )
                            : "-"}
                        </span>
                      </p>
                      <p>
                        <span className="font-semibold text-sm">Notes </span>
                        <span className="font-normal text-sm">
                          :{" "}
                          {movementData?.notes ||
                            selectedActivityDetails?.notes ||
                            "-"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </BodyScroll>
            </div>
          </div>
        )}
      </SidePanel>

      <Modal
        isOpen={cancelConfirmOpen}
        onClose={() => {
          if (!isCancellingMovement) setCancelConfirmOpen(false);
        }}
        title="Cancel this movement?"
        description="The movement status will be set to CANCELLED. This action cannot be undone."
        className="max-w-md"
      >
        <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCancelConfirmOpen(false)}
            disabled={isCancellingMovement}
          >
            Go back
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void confirmCancelMovement()}
            disabled={isCancellingMovement || !movementData?.id}
          >
            {isCancellingMovement ? "Cancelling…" : "Yes, cancel movement"}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={unlinkInvoiceTarget !== null}
        onClose={() => {
          if (!isSavingInvoices) setUnlinkInvoiceTarget(null);
        }}
        title={
          unlinkInvoiceTarget?.type === "all"
            ? "Unlink all invoices?"
            : "Remove linked invoice?"
        }
        description={
          unlinkInvoiceTarget?.type === "all"
            ? "This movement will no longer be associated with any invoices."
            : unlinkInvoiceTarget
              ? `Remove ${unlinkInvoiceTarget.reference} from this movement? Other linked invoices will stay attached.`
              : ""
        }
        className="max-w-md"
      >
        <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setUnlinkInvoiceTarget(null)}
            disabled={isSavingInvoices}
          >
            Go back
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void confirmUnlinkInvoices()}
            disabled={isSavingInvoices || !movementData?.id}
          >
            {isSavingInvoices ? "Removing…" : "Confirm"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ActivityDetailsDrawer;
